/* Marbury Lane name engraving.

   Drives the fields emitted by snippets/ml-name-engraving.liquid: how many are
   showing, whether they are valid, and how their values reach the cart.

   ---------------------------------------------------------------------------
   THE TWO PROPERTIES product-form.js READS

   For the bundle path, product-form.js collects every element matching
   [id^='CustomField-<section>-'] and emits, per cart line:

       <input name="items[n][properties][ {fieldName} ]" value="{prevValue}">

   So each field wrapper carries .fieldName and .prevValue, set here and kept in
   sync on every keystroke. Inactive fields get prevValue = '' and are skipped by
   a guard in product-form.js, so an unused bead never reaches the order.

   Those ids are rendered by Liquid, not added here, and that is deliberate.
   product-form.js loads at main-product.liquid:44 and this file loads from the
   block at ~1299; deferred scripts run in document order, so <product-form>
   captures its static NodeList BEFORE this file executes. An id added here
   would never be seen.

   The plain path needs none of this: the inputs carry form="product-form-<id>"
   so FormData picks them up directly, and disabled inputs are simply not sent.

   ---------------------------------------------------------------------------
   TWO THINGS THIS HAS TO SURVIVE

   1. SECTION RE-RENDER. Picking a different finish makes Shrine's VariantSelects
      fetch the section and swap the HTML in. Every input the shopper has typed
      into is destroyed and replaced with a blank server-rendered one. Values are
      mirrored into STORE (module scope, outlives the DOM) on every keystroke and
      restored in connectedCallback. Without this, typing three names and then
      changing the metal silently clears them.

   2. THE OPTION MIGHT NOT BE CALLED "NAMES". The Shopify product can be imported
      at any time. resolveNameOption() tries the configured hints, then falls back
      to any option whose values are all numeric. If both fail it logs once and
      shows every field rather than none — a shopper who sees five boxes can still
      order; one who sees zero cannot. */

(function () {
  'use strict';

  /* Keyed 'section|index'. Module scope on purpose — see note 1 above. */
  var STORE = {};
  var warned = false;

  function parseJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  var WORDS = {
    one: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10
  };

  /* Turn an option value into a name count. The live product writes these as
     words - "One name", "Two names", "Five name" - so digits alone are not
     enough. Handles "3", "3 names", "Three names" and "three" equally. */
  function parseCount(raw) {
    if (raw == null) return NaN;
    var s = String(raw).trim().toLowerCase();
    var digits = s.match(/\d+/);
    if (digits) return parseInt(digits[0], 10);
    var word = s.match(/\b(one|two|three|four|five|six|seven|eight|nine|ten)\b/);
    return word ? WORDS[word[1]] : NaN;
  }

  class MlNameEngraving extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.maxNames = parseInt(this.dataset.maxNames, 10) || 5;
      this.maxLength = parseInt(this.dataset.maxlength, 10) || 10;

      this.form = document.getElementById(this.dataset.form);
      this.productForm = document.getElementById('ProductForm-' + this.section)
        || this.closest('product-form');
      this.errorEl = this.querySelector('[data-ml-eng-error]');

      this.options = parseJSON(this.querySelector('[data-ml-eng-options]')) || [];
      this.variants = parseJSON(this.querySelector('[data-ml-eng-variants]')) || [];
      this.nameOptionIndex = this.resolveNameOption();

      this.fields = Array.prototype.slice.call(this.querySelectorAll('[data-ml-eng-field]'));
      this.nameCount = this.nameCountForCurrentVariant();

      this.restore();
      this.bind();
      this.apply();
    }

    disconnectedCallback() {
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      if (this.submitHandler && this.productForm) {
        this.productForm.removeEventListener('submit', this.submitHandler, true);
      }
    }

    /* ---------------------------------------------------------------- lookup */

    resolveNameOption() {
      var hints = (this.dataset.optionHints || 'Names,Number of Names,Name Count')
        .split(',')
        .map(function (h) { return h.trim().toLowerCase(); })
        .filter(Boolean);

      var names = this.options.map(function (o) {
        return (typeof o === 'string' ? o : (o && o.name) || '').trim().toLowerCase();
      });

      for (var h = 0; h < hints.length; h++) {
        var hit = names.indexOf(hints[h]);
        if (hit !== -1) return hit;
      }

      /* Fallback, and in practice the path that fires: find the option whose
         values all read as a count. The live product names this option "style"
         with values like "Three names", so neither the option name nor a digit
         test finds it. Reading the values themselves is what makes this work
         regardless of what the option ends up being called. */
      var max = this.maxNames;
      for (var i = 0; i < names.length; i++) {
        var idx = i;
        var allCounts = this.variants.length > 0 && this.variants.every(function (v) {
          var n = parseCount(v.options && v.options[idx]);
          return !isNaN(n) && n >= 1 && n <= max;
        });
        if (allCounts) return idx;
      }

      if (!warned) {
        warned = true;
        console.warn(
          '[ml-name-engraving] Could not resolve the name-count option from ' +
          JSON.stringify(names) + '. Showing all ' + this.maxNames + ' fields. ' +
          'Rename the Shopify option or set Option name hints on the block.'
        );
      }
      return -1;
    }

    nameCountForCurrentVariant() {
      if (this.nameOptionIndex === -1) return this.maxNames;
      var input = this.form && this.form.querySelector('[name="id"]');
      var id = input ? parseInt(input.value, 10) : NaN;

      var variant = null;
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].id === id) { variant = this.variants[i]; break; }
      }
      if (!variant || !variant.options) return 1;

      var raw = parseCount(variant.options[this.nameOptionIndex]);
      if (isNaN(raw) || raw < 1) return 1;
      return Math.min(raw, this.maxNames);
    }

    /* ---------------------------------------------------------------- wiring */

    bind() {
      var self = this;

      this.addEventListener('input', function (e) {
        var input = e.target.closest('.ml-eng__input');
        if (!input) return;
        self.sync(input);
        self.updateMeta(input);
        self.clearFieldError(input);
      });

      /* Three routes to a variant change, matching marbury-bundle.js: the
         theme's pubsub, a change event on the form, and a programmatic write to
         the hidden input that fires no event at all. */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          if (event && event.data && event.data.sectionId !== self.section) return;
          self.onVariantChange();
        });
      }
      if (this.form) {
        this.form.addEventListener('change', function (e) {
          if (e.target && e.target.name === 'id') self.onVariantChange();
        });
        var idInput = this.form.querySelector('[name="id"]');
        if (idInput && 'MutationObserver' in window) {
          new MutationObserver(function () { self.onVariantChange(); })
            .observe(idInput, { attributes: true, attributeFilter: ['value'] });
        }
      }

      /* Capture on <product-form> so this runs BEFORE the form's own submit
         listener inside product-form.js. stopPropagation is what actually
         prevents the add-to-cart fetch. */
      if (this.productForm) {
        this.submitHandler = function (e) {
          if (self.validate()) return;
          e.preventDefault();
          e.stopPropagation();
          if (self.errorEl) self.errorEl.hidden = false;
        };
        this.productForm.addEventListener('submit', this.submitHandler, true);
      }
    }

    onVariantChange() {
      var next = this.nameCountForCurrentVariant();
      if (next === this.nameCount) return;
      this.nameCount = next;
      this.apply();
    }

    /* ------------------------------------------------------------ persistence */

    restore() {
      var self = this;
      this.fields.forEach(function (field) {
        var input = field.querySelector('.ml-eng__input');
        if (!input) return;
        var saved = STORE[self.section + '|' + input.dataset.mlEngIndex];
        if (typeof saved === 'string') input.value = saved;
        self.updateMeta(input);
      });
    }

    /* Mirrors one input into STORE and onto the wrapper's .prevValue, which is
       what product-form.js reads at submit time. */
    sync(input) {
      var field = input.closest('[data-ml-eng-field]');
      if (!field) return;
      STORE[this.section + '|' + input.dataset.mlEngIndex] = input.value;
      field.fieldName = input.dataset.mlEngProp;
      field.prevValue = input.disabled ? '' : input.value.trim();
    }

    /* ------------------------------------------------------------------ state */

    apply() {
      var self = this;
      this.fields.forEach(function (field, idx) {
        var active = idx < self.nameCount;
        field.hidden = !active;

        var input = field.querySelector('.ml-eng__input');
        if (!input) return;
        input.disabled = !active;
        if (!active) self.clearFieldError(input);
        self.sync(input);
      });
    }

    activeInputs() {
      return Array.prototype.slice
        .call(this.querySelectorAll('.ml-eng__input'))
        .filter(function (input) { return !input.disabled; });
    }

    /* --------------------------------------------------------------- feedback */

    updateMeta(input) {
      var field = input.closest('[data-ml-eng-field]');
      var meta = field && field.querySelector('[data-ml-eng-remaining]');
      if (meta) meta.textContent = String(this.maxLength - input.value.length);
    }

    clearFieldError(input) {
      input.classList.remove('ml-eng__input--error');
      input.removeAttribute('aria-invalid');
      if (this.errorEl && !this.querySelector('.ml-eng__input--error')) {
        this.errorEl.hidden = true;
      }
    }

    validate() {
      var empties = this.activeInputs().filter(function (i) {
        return i.value.trim().length === 0;
      });
      empties.forEach(function (input) {
        input.classList.add('ml-eng__input--error');
        input.setAttribute('aria-invalid', 'true');
      });
      if (empties.length) {
        try { empties[0].focus({ preventScroll: false }); } catch (e) { empties[0].focus(); }
      }
      return empties.length === 0;
    }
  }

  if (!customElements.get('ml-name-engraving')) {
    customElements.define('ml-name-engraving', MlNameEngraving);
  }
})();
